import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Instagram, Send, Loader2, CalendarDays } from "lucide-react";
import { BRAND, WA_DEFAULT, FORM_DESTINATIONS } from "@/data/site";
import { BookingWidget } from "@/components/BookingWidget";
import { trackLead } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  {
    icon: MessageCircle,
    type: "WhatsApp Direto",
    value: BRAND.phone,
    action: "Iniciar conversa agora",
    link: WA_DEFAULT,
    testid: "contact-direct-whatsapp-link",
  },
  {
    icon: Mail,
    type: "E-mail Oficial",
    value: BRAND.email,
    action: "Enviar e-mail formal",
    link: `mailto:${BRAND.email}`,
    testid: "contact-direct-email-link",
  },
  {
    icon: Instagram,
    type: "Redes Sociais",
    value: "Instagram @melovistos • Facebook Melo Vistos",
    action: "Seguir nas redes",
    link: BRAND.instagramUrl,
    testid: "contact-direct-social-link",
  },
];

const inputCls =
  "w-full rounded-lg border border-slate-600/50 bg-navy-950/70 px-4 py-3 text-sm font-light text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-gold-400/70";

const TABS = [
  { id: "mensagem", label: "Enviar mensagem", icon: Send, testid: "contact-tab-message" },
  { id: "agendar", label: "Agendar consultoria", icon: CalendarDays, testid: "contact-tab-booking" },
];

export const Contact = () => {
  const [tab, setTab] = useState("mensagem");
  const [form, setForm] = useState({ name: "", phone: "", email: "", destination: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackLead("contact_form");
      toast.success("Solicitação enviada com sucesso!", {
        description: "Nossa equipe entrará em contato via WhatsApp e e-mail.",
      });
      setForm({ name: "", phone: "", email: "", destination: "", message: "" });
    } catch {
      toast.error("Não foi possível enviar agora.", {
        description: "Fale conosco diretamente pelo WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="border-t border-gold-400/10 bg-navy-900/40">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="mb-3 font-mono-tech text-xs tracking-[0.3em] text-gold-400">
            06 — CONTATO
          </p>
          <h2 className="font-serif-display max-w-2xl text-3xl font-normal leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Sua aprovação começa com uma{" "}
            <em className="gold-text italic">conversa.</em>
          </h2>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="space-y-4 lg:col-span-2"
          >
            {CHANNELS.map((c) => (
              <a
                key={c.type}
                data-testid={c.testid}
                href={c.link}
                target="_blank"
                rel="noopener noreferrer"
                className="card-rim group flex items-center gap-5 rounded-xl border border-gold-400/20 bg-navy-800/70 p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold-400/40 bg-gold-400/10 transition-colors duration-300 group-hover:bg-gold-400 group-hover:text-navy-950 text-gold-300">
                  <c.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">
                    {c.type.toUpperCase()}
                  </span>
                  <span className="mt-1 block text-base text-slate-100">{c.value}</span>
                  <span className="mt-1 block text-xs font-light text-gold-300">
                    {c.action} →
                  </span>
                </span>
              </a>
            ))}
            <div className="rounded-xl border border-gold-400/20 bg-navy-800/70 p-6">
              <img
                src={BRAND.logo}
                alt="Melo Vistos & Passaportes"
                className="h-16 w-16 rounded-lg border border-gold-400/40 object-cover"
              />
              <p className="mt-4 text-sm font-light leading-relaxed text-slate-400">
                {BRAND.name}
                <br />
                CNPJ {BRAND.cnpj} • São Paulo - SP - Brasil
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="rounded-2xl border border-gold-400/25 bg-navy-800/70 p-7 backdrop-blur-sm sm:p-9 lg:col-span-3"
          >
            <div className="mb-8 grid grid-cols-2 gap-2 rounded-full border border-slate-700/60 bg-navy-950/60 p-1.5">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm transition-colors duration-300 ${
                    tab === t.id
                      ? "bg-gold-400 font-semibold text-navy-950"
                      : "text-slate-300 hover:text-gold-200"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "agendar" ? (
              <BookingWidget />
            ) : (
              <form data-testid="lead-contact-form" onSubmit={submit}>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
                      NOME COMPLETO
                    </label>
                    <input
                      data-testid="contact-input-name"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Seu nome"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
                      WHATSAPP COM DDD
                    </label>
                    <input
                      data-testid="contact-input-phone"
                      required
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="(11) 90000-0000"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
                      E-MAIL
                    </label>
                    <input
                      data-testid="contact-input-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="voce@email.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
                      DESTINO DE INTERESSE
                    </label>
                    <select
                      data-testid="contact-select-destination"
                      required
                      value={form.destination}
                      onChange={set("destination")}
                      className={`${inputCls} appearance-none ${form.destination ? "" : "text-slate-500"}`}
                    >
                      <option value="" disabled>
                        Selecione o destino
                      </option>
                      {FORM_DESTINATIONS.map((d) => (
                        <option key={d} value={d} className="bg-navy-950 text-slate-100">
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
                      MENSAGEM OU DÚVIDA ESPECÍFICA
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Conte um pouco sobre sua viagem ou dúvida..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                </div>
                <button
                  data-testid="contact-form-submit-button"
                  type="submit"
                  disabled={loading}
                  className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold-400 px-6 py-3.5 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-200 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {loading ? "Enviando..." : "Solicitar análise de perfil"}
                </button>
                <p className="mt-4 text-center text-xs font-light text-slate-500">
                  Resposta em horário comercial pelo WhatsApp e pelo e-mail {BRAND.email}.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
